import { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { history } from '../../../../../store'

import { getJSON } from '../../../../../common/services/api'
import { downloadPdfData } from '../../../../../common/utils'
import {
  EmptyState,
  LabelIcon,
  NoDataIcon,
  ListItem,
  Modal,
  theme,
  PageHeader,
  Heading
} from '@lmc/ui-kit'
import {
  AddConsentDropdownButton,
  ConsentFormModal,
  ConsentPopover,
  ConsentPaywall
} from './components'
import ListItemRightChildrenNew from './components/ListItemRightChildrenNew'
import {
  ConsentFormTemplate,
  CreateConsentFormsJSON,
  RouterProps,
  ConsentType,
  ConsentStatus
} from '@lmc/common/types'
import ConsentStatusTag from 'screens/CarePlans/screens/CarePlanReviewScreen/components/ConsentSection/ConsentStatusTag'
import { Spinner } from '../../../../../components/generic'

import styled from 'styled-components'

import { isBrowser, isTablet } from 'react-device-detect'
import { ProTag } from 'components/ProTag'
import { Resident, HomeDto } from '@lmc/ts-common'
import { handleGPCInvalidate } from '../../ResidentProfileScreen/screens/GPConnectScreen/hooks/useGPdata'
import { useQueryClient } from 'react-query'
import { getNominative } from '@lmc/common/utils'

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    padding: 0px !important;
  }

  .ant-modal-content {
  }
`

type ConsentFormItem = {
  titleContent: string
  subTitleContent: string
  iconContent: string
  state: 'signed' | 'unsigned' | 'archived' | 'refused'
  id: string
  resident?: string
  consentStatus?: ConsentStatus
  type?: ConsentType
  isArchived?: boolean
}

export type ResidentConsentFormsScreenProps = {
  archiveConsentForm: (consentFormID: string, successMsg: string) => void
  consentForms: ConsentFormItem[]
  signatureItem: {
    id: string
    category: string
  }
  resident: Resident
  templates: ConsentFormTemplate[]
  fetchResidentConsentForms: (resident: string) => void
  createConsentForms: (consentForms: CreateConsentFormsJSON) => Promise<any>
  hasCarePlans: boolean
  router: RouterProps
  testId?: string
  preferredPeopleNominative?: string
}
const isDesktop = isBrowser || isTablet

export const ResidentConsentFormsScreenNew = ({
  archiveConsentForm,
  consentForms = [],
  createConsentForms,
  fetchResidentConsentForms,
  resident,
  templates,
  signatureItem,
  hasCarePlans,
  router,
  testId,
  preferredPeopleNominative
}: ResidentConsentFormsScreenProps): ReactElement => {
  const [isModalConsentVisible, setIsModalConsentVisible] =
    useState<boolean>(false)

  // Possibly to rework to more advanced flow
  const isLoading = !consentForms?.every(
    (item) => item.resident === resident?.id
  )

  useEffect(() => {
    if (resident && resident.id) fetchResidentConsentForms(resident.id)
  }, [resident, isModalConsentVisible])

  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.${key}`, variable)

  const STATUS_TO_OPTION_KEY: Record<ConsentStatus, string> = {
    [ConsentStatus.noStatus]: 'OPTION_NO_STATUS',
    [ConsentStatus.consentNotObtained]: 'OPTION_CONSENT_NOT_OBTAINED',
    [ConsentStatus.completed]: 'OPTION_COMPLETED',
    [ConsentStatus.planned]: 'OPTION_PLANNED',
    [ConsentStatus.requested]: 'OPTION_REQUESTED',
    [ConsentStatus.scheduled]: 'OPTION_SCHEDULED',
    [ConsentStatus.postponed]: 'OPTION_POSTPONED',
    [ConsentStatus.cancelled]: 'OPTION_CANCELLED',
    [ConsentStatus.withdrawn]: 'OPTION_WITHDRAWN',
    [ConsentStatus.refused]: 'OPTION_REFUSED',
    [ConsentStatus.provided]: 'OPTION_PROVIDED',
    [ConsentStatus.pending]: 'OPTION_PLANNED' // pending maps to planned
  }

  const getStatusLabel = (status: ConsentStatus): string => {
    const optionKey = STATUS_TO_OPTION_KEY[status]
    return optionKey ? t(`consent:${optionKey}`) : status
  }

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  const [consentType, setConsentType] = useState<ConsentType>('consent')

  const [formId, setFormId] = useState<string | undefined>(undefined)

  const [isPDFDownloading, setPDFDownloading] = useState<boolean>(false)

  const queryClient = useQueryClient()

  const handleArchive = (id: string) => {
    setIsModalVisible(true)
    setFormId(id)
    handleGPCInvalidate(queryClient)
  }

  const handleExportPDF = (id: string, title: string) => {
    const pdfUrl = `residents/${resident?.id}/pdf/consent/${id}`
    const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

    const pdfTitle = `${resident?.name?.first} ${resident?.name?.last}'s ${title}`

    setPDFDownloading(true)

    getJSON({ url, responseType: 'arrayBuffer' }).then((res) => {
      downloadPdfData(res, pdfTitle)
      setPDFDownloading(false)
    })
  }

  const handleView = (id: string) => {
    history.push(`/residents/${resident.id}/consent/${id}`)
    setFormId(id)
  }

  if (!hasCarePlans) {
    return (
      <ConsentPaywall
        testId={testId}
        preferredPeopleNominative={getNominative(
          preferredPeopleNominative,
          'service users'
        ).toLowerCase()}
      />
    )
  }

  if (!resident)
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-consent-forms-loading` : 'consent-forms-loading'
        }
      />
    )

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const isGpConnectEnabled = !!home?.odsCode && !!home?.asidCode

  return (
    <div
      style={styles.wrapper}
      data-testid={
        testId ? `${testId}-consent-forms-screen` : 'consent-forms-screen'
      }
    >
      <ConsentFormModal
        closeMainModal={() => setIsModalConsentVisible(false)}
        fetchResidentConsentForms={fetchResidentConsentForms}
        isModalConsentVisible={isModalConsentVisible}
        resident={resident}
        sendConsentForms={createConsentForms}
        setIsModalConsentVisible={setIsModalConsentVisible}
        templates={templates?.filter((consent) => {
          const isValidConsent =
            consent.type === consentType && !consent.isArchived
          if (isGpConnectEnabled) {
            return isValidConsent
          }
          return isValidConsent && !consent.title.includes('GP Connect')
        })}
        item={signatureItem?.id}
        category={signatureItem?.category}
        consentType={consentType}
        testId={testId}
      />
      <Modal
        localisation={{
          title: translate('components.modal.warning'),
          confirmText: t(`general.buttons.confirm`),
          cancelText: t(`general.buttons.cancel`)
        }}
        onConfirm={() => {
          // @ts-ignore
          archiveConsentForm(formId, translate('components.modal.wasArchived'))
          queryClient.invalidateQueries('care-plans')
          setIsModalVisible(false)
        }}
        onCancel={() => {
          setIsModalVisible(false)
          setFormId(undefined)
        }}
        setIsModalVisible={setIsModalVisible}
        isModalVisible={isModalVisible}
        data-testid={
          testId ? `${testId}-consent-archive-modal` : 'consent-archive-modal'
        }
      >
        <>
          <div style={styles.archiveModalRow1}>
            {translate('components.modal.row1')}
          </div>
          <div style={styles.archiveModalRow2}>
            {translate('components.modal.row2')}
          </div>
        </>
      </Modal>
      <PageHeader
        sticky
        onBack={
          !isDesktop
            ? () => history.replace?.(`/residents/${router.params.id}/profile`)
            : undefined
        }
        title={translate('title', {
          variable: `${resident?.name?.first} ${resident?.name?.last}`
        })}
        data-testid={
          testId ? `${testId}-consent-forms-header` : 'consent-forms-header'
        }
      >
        <ProTag
          data-testid={testId ? `${testId}-consent-pro-tag` : 'consent-pro-tag'}
        />
        <AddConsentDropdownButton
          addConsentForm={() => setIsModalConsentVisible(true)}
          addCapacityForm={() => setIsModalConsentVisible(true)}
          localisation={{
            addConsentForm: translate('components.dropdownBtn.addConsentForm'),
            addCapacityForm: translate('components.dropdownBtn.addCapacityForm')
          }}
          setConsentType={setConsentType}
          testId={testId}
        />
      </PageHeader>
      <div
        style={{ marginTop: 24 }}
        data-testid={
          testId ? `${testId}-consent-forms-content` : 'consent-forms-content'
        }
      >
        {consentForms?.length && !isLoading ? (
          (() => {
            const consentStatements = consentForms.filter(
              (item) => item.type === 'consent' && !item.isArchived
            )
            const lackOfCapacityStatements = consentForms.filter(
              (item) => item.type === 'lackOfCapacity' && !item.isArchived
            )
            const archivedStatements = consentForms.filter(
              (item) => item.isArchived
            )

            const renderItem = (item: ConsentFormItem, index: number) => (
              <ListItem
                key={item.id}
                iconContent={item.iconContent}
                state={item.state}
                onClick={() => handleView(item.id)}
                rightChildren={
                  isPDFDownloading && item.state !== 'archived' ? (
                    <Spinner style={{ paddingTop: 0 }} />
                  ) : (
                    <RightChildrenWrapper>
                      {item.consentStatus &&
                        item.consentStatus !== ConsentStatus.noStatus && (
                          <ConsentStatusTag
                            status={item.consentStatus}
                            label={getStatusLabel(item.consentStatus)}
                          />
                        )}
                      {item.state !== 'archived' ? (
                        <ListItemRightChildrenNew
                          popOver={
                            <ConsentPopover
                              archive={handleArchive}
                              consentFormTitle={item?.titleContent}
                              generatePDF={handleExportPDF}
                              id={item?.id}
                              view={handleView}
                            />
                          }
                          state={item.state}
                          testId={`consent-form-item-${index}-actions-btn`}
                        />
                      ) : (
                        <div style={{ width: 36, height: 20 }} />
                      )}
                    </RightChildrenWrapper>
                  )
                }
                style={styles.listItem}
                titleContent={item.titleContent}
                subTitleContent={item.subTitleContent}
                data-testid={
                  testId
                    ? `${testId}-consent-form-item-${index}`
                    : `consent-form-item-${index}`
                }
              />
            )

            return (
              <>
                {consentStatements.map((item, index) =>
                  renderItem(item, index)
                )}
                {lackOfCapacityStatements.length > 0 && (
                  <>
                    <SectionHeadingWrapper>
                      <Heading level={5}>
                        {translate('sections.lackOfCapacity')}
                      </Heading>
                    </SectionHeadingWrapper>
                    {lackOfCapacityStatements.map((item, index) =>
                      renderItem(item, consentStatements.length + index)
                    )}
                  </>
                )}
                {archivedStatements.length > 0 && (
                  <>
                    <SectionHeadingWrapper>
                      <Heading level={5}>
                        {translate('sections.archived')}
                      </Heading>
                    </SectionHeadingWrapper>
                    {archivedStatements.map((item, index) =>
                      renderItem(
                        item,
                        consentStatements.length +
                          lackOfCapacityStatements.length +
                          index
                      )
                    )}
                  </>
                )}
              </>
            )
          })()
        ) : (
          <EmptyState
            title={translate('emptyState')}
            icon={<NoDataIcon />}
            description={`${translate('youHavent')} ${resident?.name?.first} ${
              resident?.name?.last
            }.`}
            data-testid={
              testId
                ? `${testId}-consent-forms-empty-state`
                : 'consent-forms-empty-state'
            }
          />
        )}
      </div>
      <LabelIcon
        iconColor={theme.color.status.primary}
        iconName="QuestionCircleOutlined"
        onPress={() =>
          window.open(
            'http://support.logmycare.co.uk/en/articles/6014548-how-do-i-add-and-sign-consent-and-lack-of-capacity-forms',
            '_blank'
          )
        }
        style={styles.helpLink}
        text="Help"
        textColor={theme.color.status.primary}
        data-testid={
          testId ? `${testId}-consent-help-link` : 'consent-help-link'
        }
      />
    </div>
  )
}

const styles = {
  addFormsSection: {
    margin: '20px 0px 40px 0px'
  },
  archiveModalRow1: {
    fontSize: 14
  },
  archiveModalRow2: {
    fontWeight: 700,
    fontSize: 14
  },
  dropdownBtnContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: 20
  },
  header: {
    paddingBottom: 24
  },
  helpLink: {
    marginTop: 32
  },
  listItem: {
    marginBottom: 8
  },
  popOver: {
    fontSize: 9
  },
  popOverItem: {
    cursor: 'pointer',
    margin: '4px 0px'
  },
  subHeader: {
    marginBottom: 20
  },
  wrapper: {
    padding: '24px 0px 48px',
    margin: '0px auto',
    maxWidth: 1080
  }
}

const RightChildrenWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SectionHeadingWrapper = styled.div`
  margin-bottom: 16px;
`

export default ResidentConsentFormsScreenNew
